<script>
  import Button from '@components/buttons/button.svelte'
  import Text from '@components/general/text.svelte'
  import Tag from '@components/general/tag.svelte'
  import EnrollmentButton from '@components/live_sessions/enrollment-button.svelte'
  import { format, getYear } from 'date-fns'
  import { formatTime } from '@lib/utils/date'
  import Image from '@components/general/image.svelte'
  import { Calendar, Clock } from 'lucide-svelte'
  import { colors } from '@lib/utils/colors'
  import { cn } from '@lib/utils'

  let { session, label = "Upcoming session", labelArea } = $props()

  let color = $derived(session?.color || 'gray')
  let isCurrentYear = $derived(getYear(session.start_time) === getYear(new Date()))
</script>

<div class={cn('flex flex-col h-full min-h-0 group items-start w-full ring-2 ring-offset-4 rounded-lg', colors[color]?.ring, colors[color]?.hover_ring)}>

  <!-- The thumbnail area — this is the clickable link -->
  <Button variant="naked" path={`/live-sessions/${session.id}`} class="w-full">
    <div class="relative block w-full overflow-hidden rounded-lg border border-solid border-gray-200/70 bg-gray-100 aspect-video">
      <div class={cn('inset-0 z-0 h-full w-full relative', colors[color]?.gradient)}>

        {#if session.cover_image}
          <Image src={session.cover_image.thumb_url} alt={session.cover_image.name} class="absolute inset-0 w-full h-full object-cover rounded-lg" />
        {/if}

        <!-- Enrollment actions -->
        <div class="absolute top-4 left-4 z-10">
          <EnrollmentButton {session} />
        </div>

        <!-- The date -->
        <div class="absolute top-4 right-4 z-10 px-3 py-0.5 bg-white rounded-md flex flex-col gap-0.5 items-center justify-center">
          <Text as="span" class="text-2xl tracking-wide font-serif text-gray-700">{format(session.start_time, 'd')}</Text>
          <Text variant="label" as="span" class="uppercase font-light">{format(session.start_time, isCurrentYear ? 'MMM' : 'MMM yy')}</Text>
        </div>

      </div>
    </div>
  </Button>

  <!-- The event details — not a link, so tags can be independent links -->
  <div class="flex flex-1 flex-col gap-1.5 items-start px-1 py-2 w-full min-h-0">
    <Button variant="naked" path={`/live-sessions/${session.id}`} class="text-left w-full">
      <Text variant="section_title" text={session.event_name} />
    </Button>
    <div class="flex flex-wrap gap-1">
      <Tag tag={{ name: session.name, color }} size="xs" class="rounded-md px-[6px]" variant="faint" />
      {#each session.tags ?? [] as tag (tag.id)}
        <Tag {tag} size="xs" class="rounded-md px-[6px]" variant="faint" path={`/live-sessions?tag=${tag.id}`} />
      {/each}
    </div>
    <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
      <Text variant="label" as="span" class="flex items-center gap-1 text-gray-600">
        <Calendar size={12} class="text-gray-500" />
        {format(session.start_time, isCurrentYear ? 'EEE, d MMM' : 'EEE, d MMM yy')}
      </Text>
      <Text variant="label" as="span" class="flex items-center gap-1 text-gray-600">
        <Clock size={12} class="text-gray-500" />
        {formatTime(session.start_time, { mode: '12h' })}
      </Text>
    </div>

    <!-- The presenter -->
    {#if session.contributor}
      <div class="flex items-center gap-1.5 mt-auto pt-1">
        <Image src={session.contributor.image} alt={session.contributor.name} text={session.contributor.name?.charAt(0)} class="w-5 h-5 rounded-full border border-gray-200/70 bg-gray-100 text-[10px] text-gray-400" />
        <Text variant="label" as="span" class="text-gray-500">{session.contributor.name}</Text>
      </div>
    {/if}

  </div>
</div>
